import { type AnalyticsInstance, default as AnalyticsModule } from 'analytics'
import { isAndroid, isIOS, isMobile } from 'react-device-detect'
import { getRID } from '@/utils/RIDUtil'
import { isServer } from '@/utils/client'
import { getAngelEnvironment } from '@/utils/environment-utils'
import { omitUndefinedAndNulls, omitUndefineds } from '@/utils/object'
import { getObjectFromSessionStorage } from '@/utils/session-storage'
import { appName, buildId } from './constants'
import { branchAnalyticsPlugin } from './plugins/branch-plugin'
import { customAnonIdPlugin } from './plugins/custom-anon-id-plugin'
import { everflowAnalyticsPlugin } from './plugins/everflow-plugin'
// import { fullstoryAnalyticsPlugin } from './plugins/fullstory-plugin'
import { growthbookAnalyticsPlugin } from './plugins/growthbook-plugin'
import { segmentPlugin } from './plugins/segment-plugin'
import { sessionIdSyncPlugin } from './plugins/session-id-sync-plugin'
import { theatricalAngelPartytownPlugin } from './plugins/theatrical-angel-partytown-plugin'
import { getFbcCookie, getFbpCookie, regionName } from './utils'
import { getGclid } from './utils/gclid'
import { getLinkedIn } from './utils/li_fat_id'
import { getTiktokTtclid, getTiktokTtp } from './utils/tiktok'
import { getXcomTwitterClid } from './utils/xcom-twitter'

const analyticsInstance: AnalyticsInstance = AnalyticsModule({
  app: appName,
  plugins:
    typeof window === 'undefined'
      ? []
      : [
          customAnonIdPlugin(),
          sessionIdSyncPlugin(),
          everflowAnalyticsPlugin(),
          segmentPlugin(),
          theatricalAngelPartytownPlugin(),
          growthbookAnalyticsPlugin(),
          // fullstoryAnalyticsPlugin(),
          branchAnalyticsPlugin(),
        ],
})

export const analytics: AnalyticsInstance = {
  ...analyticsInstance,
  identify: async (userId, traits, options, callback) => {
    if (isServer()) {
      throw new Error('Analytics is not available on the server')
    }

    const { context, user } = analytics.getState()
    return analyticsInstance.identify(
      userId,
      {
        ...(user?.traits || {}),
        rid: getRID(), // ? braze requires rid
        did: user.anonymousId, // ? braze requires did
        anonymousId: user.anonymousId,
        userId: userId || null,
        session_id: context.sessionId,
        locale: context.locale,
        operatingSystem: context.os.name,
        referrer: context.referrer,
        timezone: context.timezone,
        userAgent: context.userAgent,
        isMobile,
        isAndroid,
        isIOS,
        fbc: getFbcCookie() || null,
        fbp: getFbpCookie() || null,
        fb_login_id: user.traits?.fb_login_id,
        twclid: getXcomTwitterClid() || null,
        ttp: getTiktokTtp() || null,
        ttclid: getTiktokTtclid() || null,
        gclid: getGclid() || null,
        li_fat_id: getLinkedIn() || null,
        ...(traits || {}),
      },
      options,
      callback,
    )
  },

  page: (data, options, callback) => {
    if (isServer()) {
      throw new Error('Analytics is not available on the server')
    }

    const { context, user } = analytics.getState()
    return analyticsInstance.page(
      omitUndefinedAndNulls({
        buildId,
        project: appName,
        utm: getObjectFromSessionStorage('originalUtms', null),
        user_id: user.userId,
        session_id: context.sessionId,
        anonymous_id: user.anonymousId,
        region: regionName(user.traits?.region),
        fbc: getFbcCookie() || null,
        fbp: getFbpCookie() || null,
        fb_login_id: user.traits?.fb_login_id,
        twclid: getXcomTwitterClid() || null,
        ttp: getTiktokTtp() || null,
        ttclid: getTiktokTtclid() || null,
        gclid: getGclid() || null,
        li_fat_id: getLinkedIn() || null,
        ...(data || {}),
      }) || undefined,
      options,
      callback,
    )
  },

  track: (
    eventName: string,
    payload?: Record<string, unknown>,
    options?: Record<string, unknown>,
    callback?: (...params: unknown[]) => unknown,
  ) => {
    if (isServer()) {
      throw new Error('Analytics is not available on the server')
    }

    const { context, user } = analytics.getState()

    const finalPayload = omitUndefineds({
      buildId,
      project: appName,
      affiliation: 'Angel Web',
      user_id: user.userId,
      session_id: context.sessionId,
      anonymous_id: user.anonymousId,
      region: regionName(user.traits?.region),
      utm: getObjectFromSessionStorage('originalUtms', null),
      browser_fingerprint_id: localStorage.getItem('browser_fingerprint_id'), // ? branch
      angel_env: getAngelEnvironment(),
      fbc: getFbcCookie() || null,
      fbp: getFbpCookie() || null,
      fb_login_id: user.traits?.fb_login_id,
      twclid: getXcomTwitterClid() || null,
      ttp: getTiktokTtp() || null,
      ttclid: getTiktokTtclid() || null,
      gclid: getGclid() || null,
      li_fat_id: getLinkedIn() || null,
      ...payload,
    })

    // fullStoryTrackEvent(eventName, { uid: user.userId, ...finalPayload })
    return analyticsInstance.track(eventName, finalPayload, options, callback)
  },
}
